import React, {useState, useEffect} from 'react';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import {Tooltip, Typography} from '@material-ui/core';
import Select from 'react-select';
import {getSSPLogo} from '../../helpers/getSSPLogo';
import useStore from '../../../store/store';

const useStyles = makeStyles(theme => ({
  selectContainer: {
    minWidth: 400,
    display: 'inline-block',
  },
  sspIcon: {
    height: 16,
    width: 16,
    marginRight: 8,
    verticalAlign: 'middle',
  },
  stackedIconsContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    height: 20,
  },
  stackedIcon: {
    height: 16,
    width: 16,
    borderRadius: 4,
    background: theme.palette.graphColors.themeLightGrayOpaque,
    padding: 2,
    marginLeft: -4, // Overlap by 2px
    '&:first-child': {
      marginLeft: 0, // First icon has no overlap
    },
  },
}));

const BidderSelect = ({
  bidders,
  selectedBidder,
  onBidderChange,
  attributeGroups,
  directSSPCodeAndAlias,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const setState = useStore(state => state.setState);

  // Disable typing for account selector when this dropdown is open
  useEffect(() => {
    setState('disableTypingForAccountSelector', menuIsOpen);
  }, [menuIsOpen, setState]);

  // Get stacked SSP logos for Combined data display (memoized for stable random selection)
  const stackedLogos = React.useMemo(() => {
    if (!bidders) return [];

    const uniqueLogos = bidders
      .filter(bidder => bidder !== 'Combined data')
      .map(bidder => getSSPLogo(bidder, attributeGroups))
      .filter(logo => logo !== null)
      .filter((logo, index, arr) => arr.indexOf(logo) === index); // Remove duplicates

    // Shuffle array and take first 5
    const shuffled = [...uniqueLogos].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 5);
  }, [bidders, attributeGroups]);

  // Create react-select options with SSP icons
  const createBidderOptions = () => {
    if (!bidders) return [];

    return bidders.map(bidder => {
      let label = bidder;
      let logo = null;
      let isDirect = false;

      if (bidder === 'Combined data') {
        label = 'All SSPs combined';
        // For combined data, we'll handle the stacked logos in formatOptionLabel
      } else {
        // Get SSP logo using attributeGroups for all bidders including those from directSSPCodeAndAlias
        logo = getSSPLogo(bidder, attributeGroups);

        // Check if this bidder is in directSSPCodeAndAlias
        if (directSSPCodeAndAlias && directSSPCodeAndAlias[bidder.toLowerCase()]) {
          isDirect = true;
        }
      }

      return {
        value: bidder,
        label: label.replace(/S2S/i, ' S2S').replace(/_/i, ' '),
        logo: logo,
        isDirect: isDirect,
        directValue: isDirect ? directSSPCodeAndAlias[bidder.toLowerCase()] : null,
      };
    });
  };

  // React-select styles
  const selectStyles = {
    container: provided => ({...provided, minWidth: '100%', marginTop: 0}),
    input: provided => ({...provided, color: 'inherit'}),
    control: (provided, state) => ({
      ...provided,
      minWidth: '100%',
      marginTop: 0,
      border: 'none',
      background: theme.palette.graphColors.themeDarkGray,
      outline: 'none',
      boxShadow: 'none',
      '&:hover': {
        border: 'none',
        boxShadow: 'none',
      },
    }),
    singleValue: provided => ({...provided, color: 'inherit', textTransform: 'capitalize'}),
    menu: provided => ({
      ...provided,
      boxShadow: '0 1px 20px rgba(0, 0, 0, .1)',
      background: theme.palette.background.paper,
    }),
    option: (provided, state) => {
      return {
        ...provided,
        cursor: 'pointer',
        color: 'inherit',
        textTransform: 'capitalize',
        opacity: state.isFocused ? 1 : 0.8,
        backgroundColor: state.isFocused ? 'rgba(199, 199, 199, .05)' : 'none',
        '&:active': {
          background: 'none',
          opacity: 1,
        },
        '&:hover': {
          backgroundColor: 'none',
          opacity: 1,
        },
      };
    },
  };

  // Format option label to show SSP icon
  const formatOptionLabel = ({value, label, logo, isDirect, directValue}) => {
    let logosToShow = [];
    let filteredBidders = [];

    let artificialOptions = ['Combined data', 'Client Side Prebid', 'Server Side Prebid'];

    if (artificialOptions.includes(value)) {
      if (value === 'Combined data') {
        filteredBidders = bidders;
      } else if (value === 'Client Side Prebid') {
        filteredBidders = bidders.filter(bidder => !bidder.toLowerCase().includes('s2s'));
      } else if (value === 'Server Side Prebid') {
        filteredBidders = bidders.filter(bidder => bidder.toLowerCase().includes('s2s'));
      }
      logosToShow = filteredBidders
        .map(bidder => getSSPLogo(bidder, attributeGroups))
        .filter(logo => logo !== null)
        .filter((logo, index, arr) => arr.indexOf(logo) === index); // Remove duplicates
      // Take first 5
      logosToShow = logosToShow.slice(0, 5);
    }

    return (
      <div style={{display: 'flex', alignItems: 'center'}}>
        {artificialOptions.includes(value) && logosToShow.length > 0 ? (
          <div style={{display: 'flex', alignItems: 'center', marginRight: 8}}>
            <Tooltip title="All SSPs combined" arrow>
              <div className={classes.stackedIconsContainer}>
                {logosToShow.map((logoSrc, index) => (
                  <img
                    key={index}
                    src={logoSrc}
                    alt={`SSP ${index + 1}`}
                    className={classes.stackedIcon}
                  />
                ))}
              </div>
            </Tooltip>
            {logosToShow.length > 0 && filteredBidders.length > logosToShow.length && (
              <Typography
                variant="caption"
                display="inline-block"
                style={{
                  padding: 2,
                  background: theme.palette.background.default,
                  position: 'relative',
                  fontSize: 8,
                  marginLeft: 4,
                  borderRadius: 2,
                  lineHeight: '1',
                }}
              >
                +{filteredBidders.length - logosToShow.length}
              </Typography>
            )}
          </div>
        ) : (
          logo && <img src={logo} alt={label} className={classes.sspIcon} />
        )}
        <span>{label}</span>
        {isDirect && directValue && (
          <Tooltip title={`Direct on ${directValue.join(', ')}.`} arrow>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 4,
                borderRadius: 4,
                marginLeft: 8,
                position: 'relative',
                maxHeight: 20,
                lineHeight: '20px',
                verticalAlign: 'middle',
              }}
            >
              <Typography
                variant="caption"
                display="inline-block"
                style={{
                  color: theme.palette.primary.contrastText,
                  background: theme.palette.graphColors.eCPM,
                  padding: '2px 6px',
                  borderRadius: 3,
                  fontWeight: 'bold',
                  fontSize: 9,
                  lineHeight: '1',
                }}
              >
                DIRECT
              </Typography>
              {directValue.length > 1 && (
                <Typography
                  variant="caption"
                  display="inline-block"
                  style={{
                    padding: 2,
                    background: theme.palette.background.default,
                    position: 'relative',
                    fontSize: 8,
                    marginLeft: 2,
                    borderRadius: 2,
                    lineHeight: '1',
                  }}
                >
                  {directValue.length}
                </Typography>
              )}
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  if (!bidders) return null;

  return (
    <div className={classes.selectContainer}>
      <Select
        value={createBidderOptions().find(option => option.value === selectedBidder)}
        onChange={onBidderChange}
        options={createBidderOptions()}
        styles={selectStyles}
        formatOptionLabel={formatOptionLabel}
        placeholder="Select bidder"
        isClearable={false}
        onMenuOpen={() => setMenuIsOpen(true)}
        onMenuClose={() => setMenuIsOpen(false)}
      />
    </div>
  );
};

export default BidderSelect;
